/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { UserOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { ROUTES } from '@sportninja/common/constants/app'
import { NO_PLAYER_PROFILE_GUIDANCE } from '@sportninja/common/features/profile/finderSettings/guidance'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { enablePlayerTeamFinder } from '@sportninja/common/utils/utils'
import { Link } from 'react-router-dom'
import Text from 'src/components/ts-components/Text'
import { weightSemiBold } from 'src/components/css'

/**
 * Profile empty state shown when the user has no associated player profiles.
 * View-only — the gating decision lives in ProfilePage via
 * `hasNoAssociatedPlayers`. SN-8294: lists the two ways a player profile comes
 * about, and points to Team Finder. SN-8453: where Team Finder is off
 * (SportNinja) only the invite bullet shows.
 */
const NoPlayerProfile = () => {
  const { invite, register, action } = NO_PLAYER_PROFILE_GUIDANCE

  return (
    <div css={cardStyle}>
      <div css={contentStyle}>
        <UserOutlined css={iconStyle} />
        <Text variant='paragraphLarge' weight='medium' color={colors.NEUTRAL_0}>
          {capitalizeWords(NO_PLAYER_PROFILE_GUIDANCE.title)}
        </Text>
        <ul css={waysListStyle}>
          <li css={wayItemStyle}>
            <span aria-hidden='true'>•</span>
            <Text variant='paragraphSmall' color={colors.NEUTRAL_50}>
              <span css={wayLabelStyle}>{invite.label}</span> - {invite.body}
            </Text>
          </li>
          {enablePlayerTeamFinder ? (
            <li css={wayItemStyle}>
              <span aria-hidden='true'>•</span>
              <Text variant='paragraphSmall' color={colors.NEUTRAL_50}>
                <span css={wayLabelStyle}>{register.label}</span> -{' '}
                {register.body}{' '}
                <Link
                  css={linkStyle}
                  to={ROUTES.TEAM_FINDER}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {capitalizeWords(action)}
                </Link>
              </Text>
            </li>
          ) : null}
        </ul>
      </div>
    </div>
  )
}

export default NoPlayerProfile

const cardStyle = css`
  width: 100%;
  min-height: 250px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  border-radius: 8px;
  background-color: ${colors.SECONDARY_400};
`

const contentStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  max-width: 600px;
  text-align: center;
`

// Left-aligned bullets inside the centred card. The global reset strips list
// markers, so each item draws its own muted "•" (same as mobile); the ul/li
// keep list semantics for screen readers.
const waysListStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding: 0;
  text-align: left;
  color: ${colors.NEUTRAL_50};
`

const wayItemStyle = css`
  display: flex;
  gap: 6px;
`

const iconStyle = css`
  font-size: 40px;
  color: ${colors.NEUTRAL_100};
`

// The bullet's lead-in — brighter and heavier than the muted body after it.
const wayLabelStyle = css`
  ${weightSemiBold}
  color: ${colors.NEUTRAL_0};
`

// Same inline Team Finder link as the Player Finder guidance in PreferenceModal.
// Opens in a new tab so the profile stays where it was.
const linkStyle = css`
  color: ${colors.PRIMARY};
  text-decoration: underline;
  white-space: nowrap;
`
